import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { X, Check } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const PLATFORM = [
  "An AI filter or printed reproduction",
  "A 24-hour turnaround promise",
  "They have never met your pet",
  "Ink on demand, identical every time",
];

const PETRAITS = [
  "A real consultation — in person, by phone, or email",
  "Your headshot and candid photos, studied closely",
  "Layered, hand-brushed texture you can feel",
  "A signed, one-of-a-kind original painting",
];

const NameChip = ({ children }) => (
  <span className="absolute bottom-3 left-3 px-3 py-1.5 rounded-full bg-[#0C0A09]/80 backdrop-blur-sm border border-stone-700 text-[11px] font-mono tracking-[0.15em] uppercase text-[#BDE9FA]">
    {children}
  </span>
);

export const Method = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yA = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const yB = useTransform(scrollYProgress, [0, 1], [120, -40]);
  const yC = useTransform(scrollYProgress, [0, 1], [30, -90]);

  return (
    <section id="method" data-testid="method-section" ref={ref} className="relative py-28 sm:py-36 overflow-hidden">
      <div className="absolute inset-0 spotlight" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        <div>
          <Reveal>
            <Eyebrow>The Proprietary Method</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-serif tracking-tight leading-[1.08] text-3xl sm:text-4xl lg:text-5xl text-[#EAF5FA]">
              What no platform can <span className="gold-foil-text italic">mass-produce</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base sm:text-lg text-stone-300 leading-relaxed">
              Sites boasting super-speedy turnarounds sell you a printout. A PETRAITS original begins
              the opposite way — slowly, personally, with a conversation about who your companion
              really is. Only then does the brush touch the canvas.
            </p>
          </Reveal>

          <div className="mt-10 space-y-8">
            <Reveal delay={0.25}>
              <div data-testid="method-platform-list" className="space-y-3">
                <p className="text-xs uppercase tracking-[0.25em] font-mono text-stone-500">The big platforms</p>
                {PLATFORM.map((t) => (
                  <div key={t} className="flex items-start gap-3 text-stone-500">
                    <X className="w-4 h-4 mt-1 shrink-0 text-[#C85A32]" />
                    <span className="text-sm line-through decoration-stone-700">{t}</span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.35}>
              <div data-testid="method-petraits-list" className="space-y-3 p-6 rounded-2xl bg-[#1F1B17] border border-[#2E2721]">
                <p className="text-xs uppercase tracking-[0.25em] font-mono text-[#41C2F2]">The PETRAITS atelier</p>
                {PETRAITS.map((t) => (
                  <div key={t} className="flex items-start gap-3 text-stone-200">
                    <Check className="w-4 h-4 mt-1 shrink-0 text-[#41C2F2]" />
                    <span className="text-sm">{t}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        <div className="relative h-[560px] sm:h-[680px]" data-testid="method-mockups">
          <motion.div style={{ y: yA }} className="absolute left-0 top-0 w-[46%] rotate-[-3deg] z-10">
            <div className="relative frame-mat p-2 rounded-sm">
              <img src="/mockups/henry.webp" alt="Henry's portrait hanging in a bright foyer" className="w-full h-auto rounded-[2px]" loading="lazy" />
              <NameChip>Henry · the foyer</NameChip>
            </div>
          </motion.div>
          <motion.div style={{ y: yB }} className="absolute right-0 top-6 w-[56%] rotate-[2deg]">
            <div className="relative frame-mat p-2 rounded-sm">
              <img src="/mockups/endora.png" alt="Endora's portrait above a red couch" className="w-full h-auto rounded-[2px]" loading="lazy" />
              <NameChip>Endora · the red couch</NameChip>
            </div>
          </motion.div>
          <motion.div style={{ y: yC }} className="absolute right-[8%] bottom-0 w-[52%] rotate-[-2deg] z-20">
            <div className="relative frame-mat p-2 rounded-sm">
              <img src="/mockups/shababy.png" alt="Shababy's portrait in a sage green salon" className="w-full h-auto rounded-[2px]" loading="lazy" />
              <NameChip>Shababy · the salon</NameChip>
            </div>
          </motion.div>
          <div className="absolute -left-6 bottom-10 font-serif italic text-[#41C2F2]/25 text-8xl select-none" aria-hidden="true">Est.</div>
        </div>
      </div>
    </section>
  );
};
